import { createRouter, createWebHistory } from 'vue-router';
import type {
  RouteRecordRaw,
  Router,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric,
  NavigationGuardNext
} from 'vue-router';

import authRoutes from '@/router/authRoutes.ts';
import feesRoutes from "@/router/feesRoutes.ts";
import mandateRoutes from "@/router/mandateRoutes.ts";
import userRoutes from "@/router/userRoutes.ts";
import supportRoutes from "@/router/supportRoutes.ts";
import legalDocumentRoutes from "@/router/legalDocumentRoutes.ts";
import referralRewardRoutes from "@/router/referralRewardRoutes.ts";
import identityDuplicateReviewRoutes from "@/router/identityDuplicateReviewRoutes.ts";
import faqRoutes from "@/router/faqRoutes.ts";

import { useAuthStore } from '@/stores/authStore.ts';
import { useNavigationLoading } from '@/stores/useNavigationLoading.ts';
import { appConfig } from '@/types/appConfig.type.ts';
import AppLayout from "@/layouts/AppLayout.vue";

import HomeView from '../views/HomeView.vue';
import UnauthorizedView from "@/views/redirects/UnauthorizedView.vue";
import bankingOperationsRoutes from "@/router/bankRoutes.ts";

const routes: RouteRecordRaw[] = [
  ...authRoutes,
  ...mandateRoutes,
  ...bankingOperationsRoutes,
  ...userRoutes,
  ...feesRoutes,
  ...supportRoutes,
  ...faqRoutes,
  ...legalDocumentRoutes,
  ...referralRewardRoutes,
  ...identityDuplicateReviewRoutes,
  {
    path: '/',
    component: HomeView,
    meta: {
      layout: AppLayout,
      requiresAuth: true
    },
    beforeEnter: (to: RouteLocationNormalizedGeneric,
                  _: RouteLocationNormalizedLoadedGeneric,
                  next: NavigationGuardNext) => {
      const authStore = useAuthStore()

      const protectedRoutes = routes.filter(route => route.meta?.requiresAuth)

      const firstAccessibleRoute = protectedRoutes.find(route => {
        const permissions = route.meta?.permissions as string[] | undefined
        return !(route.meta?.permissions && !authStore.hasPermission(permissions))
      })

      if (firstAccessibleRoute) {
        return next(firstAccessibleRoute.path)
      }

      next('/login')
    }
  },
  {
    path: '/unauthorized',
    component: UnauthorizedView,
    meta: {
      layout: AppLayout,
      requiresAuth: true
    },
  }
]

const router: Router = createRouter({
  history: createWebHistory(appConfig.BASE_URL),
  routes
})

// Some routes resolve several API calls in their `beforeEnter` guard (e.g. the client detail
// page, which loads the user plus 4 enrichment blocks) before Vue Router actually switches the
// view. Without a global indicator the app just sits on the previous screen with no feedback
// while that resolves. This must be registered before the auth/permissions guard below so it
// starts as early as possible in the navigation, and covers `beforeEnter` too since `afterEach`
// only fires once the whole navigation (guards included) has resolved.
router.beforeEach(() => {
  useNavigationLoading().startLoading()
})

router.afterEach(() => {
  useNavigationLoading().stopLoading()
})

router.onError(() => {
  useNavigationLoading().stopLoading()
})

router.beforeEach((to: RouteLocationNormalizedGeneric,
                   _: RouteLocationNormalizedLoadedGeneric,
                   next: NavigationGuardNext): void => {
  const authStore = useAuthStore()
  const authenticated: boolean = authStore.auth.authenticated

  if (to.meta['requiresAuth'] && !authenticated) {
    next('/login')
    return
  }

  if (!to.meta['requiresAuth'] && authenticated) {
    next('/')
    return
  }

  if (to.meta['permissions'] && !authStore.hasPermission(to.meta['permissions'] as string[])) {
    next('/unauthorized')
    return
  }

  next()
})

export function getRoutes(): RouteRecordRaw[] {
  return routes;
}

export default router
